// Google Maps Distance Matrix API utility for calculating travel times

// Simple type declaration for Google Maps JavaScript API
declare global {
  interface Window {
    google: any;
  }
}

interface TravelTime {
  driving?: string;
  transit?: string;
  bicycling?: string;
  walking?: string;
}

interface DistanceMatrixElement {
  distance?: {
    text: string;
    value: number;
  };
  duration?: {
    text: string;
    value: number;
  };
  status: string;
}

interface DistanceMatrixResponse {
  status: string;
  rows: {
    elements: DistanceMatrixElement[];
  }[];
}

/**
 * Calculate travel times between two locations using Google Maps JavaScript API
 * @param origin Origin address string
 * @param destination Destination address string
 * @param apiKey Google Maps API key (not used for JS API but kept for consistency)
 * @param transportationModes Array of transportation modes to check
 * @returns Promise<TravelTime> Object with travel times for each mode
 */
export async function calculateTravelTimes(
  origin: string,
  destination: string,
  apiKey: string,
  transportationModes: string[] = ['driving', 'transit', 'bicycling', 'walking']
): Promise<TravelTime> {
  if (!origin || !destination || origin === destination) {
    return {};
  }

  // Check if Google Maps JavaScript API is loaded
  if (typeof window === 'undefined' || !window.google || !window.google.maps) {
    console.error('Google Maps JavaScript API not loaded');
    return {};
  }

  const travelTimes: TravelTime = {};

  // Map transportation types from database to Google Maps API modes
  const modeMapping: { [key: string]: any } = {
    'driving': window.google.maps.TravelMode.DRIVING,
    'public transit': window.google.maps.TravelMode.TRANSIT,
    'cycling': window.google.maps.TravelMode.BICYCLING,
    'walking': window.google.maps.TravelMode.WALKING
  };

  const service = new window.google.maps.DistanceMatrixService();

  try {
    for (const dbMode of transportationModes) {
      const googleMode = modeMapping[dbMode];
      if (!googleMode) continue;
      
      try {
        const result = await new Promise<any>((resolve, reject) => {
          const request: any = {
            origins: [origin],
            destinations: [destination],
            travelMode: googleMode,
            unitSystem: window.google.maps.UnitSystem.METRIC,
            avoidHighways: false,
            avoidTolls: true
          };

          // For transit, add departure time to get accurate estimates
          if (googleMode === window.google.maps.TravelMode.TRANSIT) {
            request.transitOptions = {
              departureTime: new Date()
            };
          }

          service.getDistanceMatrix(request, (response: any, status: any) => {
            if (status === window.google.maps.DistanceMatrixStatus.OK && response) {
              resolve(response);
            } else {
              reject(new Error(`Distance Matrix API failed with status: ${status}`));
            }
          });
        });

        if (result.rows && result.rows.length > 0 && result.rows[0].elements.length > 0) {
          const element = result.rows[0].elements[0];
          
          if (element.status === window.google.maps.DistanceMatrixElementStatus.OK && element.duration) {
            // Store the travel time using the API format for consistency with UI
            const apiFormat = dbMode === 'public transit' ? 'transit' : 
                             dbMode === 'cycling' ? 'bicycling' : dbMode;
            travelTimes[apiFormat as keyof TravelTime] = element.duration.text;
          }
        }
      } catch (error) {
        console.warn(`Failed to calculate travel time for mode ${dbMode}:`, error);
        // Continue with other modes even if one fails
      }
    }
  } catch (error) {
    console.error('Error calculating travel times:', error);
  }

  return travelTimes;
}

/**
 * Format travel time to a consistent format (e.g., "15 min", "1 hour 23 min")
 * @param duration Duration text from Google Maps API
 * @returns Formatted time string
 */
export function formatTravelTime(duration: string): string {
  if (!duration) return 'N/A';
  
  // Google Maps typically returns formats like "15 mins", "1 hour 23 mins"
  // Standardize to "min" instead of "mins" for consistency
  return duration.replace(/mins?/g, 'min').replace(/hours?/g, 'hr');
}